import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatMoney, type StatementFinancial, toCents } from '@k-management/shared';
import { Link } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { announceTreasuryChange, movementPath } from './treasury-ui.js';

/**
 * The financial part of the account statement (2.22): what was paid to the
 * subject, or received from him, without a document yet, and the loans with
 * him. A part still unused is taken away from here; it is used from the
 * invoice when it arrives.
 */
export function StatementFinancialSection({
  financial,
  bankName,
}: {
  financial: StatementFinancial;
  bankName: (code: string) => string;
}) {
  const queryClient = useQueryClient();
  const canWrite = useCan('tesoreria.write');
  const remove = useMutation({
    mutationFn: (id: string) =>
      apiRequest(`/treasury/subject-payments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      announceTreasuryChange();
      void queryClient.invalidateQueries({ queryKey: ['treasury'] });
    },
  });
  if (!financial.payments.length && !financial.loans.length) return null;
  return (
    <div className="card stack">
      <div className="row between">
        <h2>Finanziaria</h2>
        <div className="row small">
          {toCents(financial.availableOut) > 0 && (
            <span>
              Pagati senza documento: <strong>{formatMoney(financial.availableOut)}</strong>
            </span>
          )}
          {toCents(financial.availableIn) > 0 && (
            <span>
              Incassati senza documento: <strong>{formatMoney(financial.availableIn)}</strong>
            </span>
          )}
        </div>
      </div>
      {financial.payments.length > 0 && (
        <>
          <h3>Pagamenti e incassi senza documento</h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Banca</th>
                  <th>Movimento</th>
                  <th className="numeric">Importo</th>
                  <th className="numeric">Disponibile</th>
                  <th>Note</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {financial.payments.map((item) => (
                  <tr key={item.id}>
                    <td>{formatDate(item.date)}</td>
                    <td>
                      {item.card ? (
                        // A top-up with a card (2.25): the link opens the spending.
                        <Link
                          to={`/tesoreria/carte?${new URLSearchParams({
                            spesa: item.movementId,
                            data: item.date,
                            carta: item.card.cardId,
                          }).toString()}`}
                        >
                          Carta •••• {item.card.last4}
                        </Link>
                      ) : (
                        <Link to={movementPath(item.accountCode, item.movementId)}>
                          {bankName(item.accountCode)}
                        </Link>
                      )}
                    </td>
                    <td className="small">
                      {item.direction === 'out' ? 'Pagato' : 'Incassato'} ·{' '}
                      {item.description.slice(0, 90)}
                    </td>
                    <td className="numeric">{formatMoney(item.amount)}</td>
                    <td className="numeric">
                      {item.withoutInvoice ? (
                        <span className="pill muted">senza fattura · costo</span>
                      ) : (
                        <strong>{formatMoney(item.available)}</strong>
                      )}
                    </td>
                    <td className="small">
                      {[item.reference, item.note].filter(Boolean).join(' · ')}
                    </td>
                    <td>
                      {canWrite &&
                        (item.withoutInvoice ||
                          toCents(item.available) === toCents(item.amount)) && (
                          <button
                            type="button"
                            className="link small"
                            disabled={remove.isPending}
                            onClick={() => remove.mutate(item.id)}
                          >
                            Togli
                          </button>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {remove.error && <p className="error">{remove.error.message}</p>}
          <p className="muted small">
            Quando arriva la fattura, dalla sua scheda si usa con «Usa»: il pagamento passa sulla
            fattura come movimento di banca, con la sua data.
          </p>
        </>
      )}
      {financial.loans.length > 0 && (
        <>
          <h3>Finanziamenti con il soggetto</h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Finanziamento</th>
                  <th className="numeric">Erogato</th>
                  <th className="numeric">Restituito</th>
                  <th className="numeric">Residuo</th>
                </tr>
              </thead>
              <tbody>
                {financial.loans.map((loan) => (
                  <tr key={loan.id}>
                    <td>
                      <Link to={`/tesoreria/mutui/${loan.id}`}>{loan.name}</Link>
                    </td>
                    <td className="numeric">{loan.amount ? formatMoney(loan.amount) : '—'}</td>
                    <td className="numeric">{formatMoney(loan.repaid)}</td>
                    <td className="numeric">{loan.residual ? formatMoney(loan.residual) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
